import {
  FormulaValidationResponse,
  FundWaterfallConfigResponse,
  FundWaterfallResponse,
  FundWaterfallResultsResponse,
  UpdateWaterfallConfigResponse,
  WaterfallConfigPayload,
} from '../../data-models/fund-waterfall.data-model';
import { FundDataModel } from '../../schemas/Fund.schema';
import {
  CapitalAllocationResponse,
  ProjectedFeesAndExpensesTransaction,
} from '../../schemas/FundCapitalAllocation.schema';
import { FundData } from '../../schemas/FundData.schema';
import { FundFieldOverride } from '../../schemas/FundFieldOverride.schema';
import { FundProjectedInvestmentAudit } from '../../schemas/FundProjectedInvestmentAudit.schema';
import { FundInvestment, FundReserves } from '../../schemas/FundReserves.schema';
import { MaggieHttp } from '../MaggieHttp';
import { getPlainDateString } from './MaggieMetricsQueries';

export interface IUpdateFundFields extends Partial<FundDataModel> {
  id: number;
}

export async function updateFund(fund: IUpdateFundFields): Promise<FundDataModel> {
  const response = await MaggieHttp.maggie.put<IUpdateFundFields, FundDataModel>(`/fund/${fund.id}`, fund);
  return response.data;
}

// Null clears a value (the API deletes its row); customDataDate dates time-series writes.
export type FundCustomDataPayload = Record<string, string | number | string[] | null>;

export async function updateFundCustomData(
  fundId: number,
  customData: FundCustomDataPayload,
  customDataDate?: string
): Promise<FundDataModel> {
  const response = await MaggieHttp.maggie.put<
    { id: number; customData: FundCustomDataPayload; customDataDate?: string },
    FundDataModel
  >(`/fund/${fundId}`, { id: fundId, customData, customDataDate });
  return response.data;
}
export async function fetchFundData(fundId: number): Promise<FundData[]> {
  const response = await MaggieHttp.maggie.get<FundData[]>(`/fund/${fundId}/data`);
  return response.data;
}

export interface IUpdateFundDataPayload
  extends
    Partial<Pick<FundData, 'netAssets' | 'deemedContributions' | 'contributedSecurities'>>,
    Pick<FundData, 'id' | 'fundId' | 'date'> {}

export async function updateFundData(fundData: IUpdateFundDataPayload): Promise<FundData> {
  const response = await MaggieHttp.maggie.put<IUpdateFundDataPayload, FundData>(
    `/fund/data/${fundData.id}`,
    fundData
  );
  return response.data;
}
export interface ICreateFundDataPayload
  extends
    Partial<Pick<FundData, 'netAssets' | 'deemedContributions' | 'contributedSecurities'>>,
    Pick<FundData, 'fundId' | 'date'> {}
export async function createFundData(fundData: ICreateFundDataPayload): Promise<FundData> {
  const response = await MaggieHttp.maggie.post<ICreateFundDataPayload, FundData>(`/fund/data`, fundData);
  return response.data;
}

export async function fetchFundReserves(date: Date, fundId: number): Promise<FundReserves> {
  const response = await MaggieHttp.maggie.get<FundReserves>(`/fund/${fundId}/reserves`, {
    params: { asOfDate: getPlainDateString(date) },
  });
  return response.data;
}

export async function getFundProjectedInvestmentHistory(
  fundId: number
): Promise<FundProjectedInvestmentAudit[]> {
  const response = await MaggieHttp.maggie.get<FundProjectedInvestmentAudit[]>(
    `/fund/${fundId}/reserves-history`
  );
  return response.data;
}

export async function createFundInvestment(investment: FundInvestment): Promise<FundInvestment> {
  const res = await MaggieHttp.maggie.post<FundInvestment, FundInvestment>(
    '/fund-projected-investment',
    investment
  );
  return res.data;
}

export async function updateFundInvestment(investment: FundInvestment): Promise<FundInvestment> {
  const res = await MaggieHttp.maggie.put<FundInvestment, FundInvestment>(
    `/fund-projected-investment/${investment.id!}`,
    investment
  );
  return res.data;
}

export async function deleteFundInvestment(investmentId: number) {
  await MaggieHttp.maggie.delete(`/fund-projected-investment/${investmentId}`);
}

interface IFetchCapitalAllocationParams {
  id: number;
  asOfDate: string;
}
export async function fetchCapitalAllocation({ id, asOfDate }: IFetchCapitalAllocationParams) {
  const res = await MaggieHttp.maggie.get<CapitalAllocationResponse>(`/fund/${id}/capital-allocation`, {
    params: { asOfDate },
  });
  return res?.data;
}

export async function createProjectedFeesAndExpensesTransaction(
  transaction: Partial<ProjectedFeesAndExpensesTransaction>
) {
  const res = await MaggieHttp.maggie.post<
    Partial<ProjectedFeesAndExpensesTransaction>,
    ProjectedFeesAndExpensesTransaction
  >('/fund-projected-fees-and-expenses/', transaction);
  return res.data;
}

export async function updateProjectedFeesAndExpensesTransaction(
  transaction: Partial<ProjectedFeesAndExpensesTransaction>
) {
  if (!transaction.id) {
    throw new Error('Missing transaction id');
  }
  const res = await MaggieHttp.maggie.put<
    Partial<ProjectedFeesAndExpensesTransaction>,
    ProjectedFeesAndExpensesTransaction
  >(`/fund-projected-fees-and-expenses/${transaction.id!}`, transaction);
  return res.data;
}

export async function deleteProjectedFeesAndExpensesTransaction(transactionId: number) {
  await MaggieHttp.maggie.delete(`/fund-projected-fees-and-expenses/${transactionId}`);
}

export async function bulkOverrideFundFields(overrides: FundFieldOverride[]) {
  const { data } = await MaggieHttp.maggie.post<{ overrides: FundFieldOverride[] }, FundFieldOverride[]>(
    '/override/runtime',
    { overrides }
  );
  return data;
}

export async function updateFundWaterfallConfig(
  fundId: number,
  config: WaterfallConfigPayload
): Promise<UpdateWaterfallConfigResponse> {
  const { data } = await MaggieHttp.maggie.post<WaterfallConfigPayload, UpdateWaterfallConfigResponse>(
    `waterfall/fund/${fundId}/waterfall/config`,
    config
  );
  return data;
}

export async function getFundWaterfallConfig(fundId: number): Promise<FundWaterfallConfigResponse> {
  const { data } = await MaggieHttp.maggie.get<FundWaterfallConfigResponse>(
    `waterfall/fund/${fundId}/waterfall/config`
  );
  return data;
}

export async function getFundWaterfallResults(
  fundId: number,
  asOfDate: string
): Promise<FundWaterfallResultsResponse> {
  const { data } = await MaggieHttp.maggie.get<FundWaterfallResultsResponse>(
    `waterfall/fund/${fundId}/waterfall/results`,
    {
      params: { asOfDate },
    }
  );
  return data;
}

export async function getFundWaterfall(fundId: number, asOfDate: string): Promise<FundWaterfallResponse> {
  const { data } = await MaggieHttp.maggie.get<FundWaterfallResponse>(`waterfall/fund/${fundId}/waterfall`, {
    params: { asOfDate },
  });
  return data;
}

export async function validateWaterfallFormula(formula: string): Promise<FormulaValidationResponse> {
  const { data } = await MaggieHttp.maggie.post<{ formula: string }, FormulaValidationResponse>(
    'waterfall/fund/waterfall/config/validate',
    { formula }
  );
  return data;
}
